import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Rocket, User, Check, ExternalLink } from 'lucide-react';
import OnboardingForm from './OnboardingForm';

const APP_URL = 'https://app.rentid.io';

const STARTUP_CHECKLIST = [
  {
    title: 'Business details',
    desc: 'Legal business name, EIN, business address, and contact email/phone.',
  },
  {
    title: 'Stripe account',
    desc: 'An active Stripe account to accept payments and payouts. You can create one in the app.',
  },
  {
    title: 'Vehicle information',
    details: [
      { label: 'Starter Members', text: 'Vehicle VIN, photos and the daily rates you want to charge.' },
      { label: 'Pro Plan Members', text: 'Picture of license plate and vehicle photos.' },
    ],
  },
];

const Chooser = ({ onSelect }) => (
  <div>
    <div className="preob-chooser__head">
      <h1 className="preob-chooser__title">Let's get your fleet live</h1>
      <p className="preob-chooser__sub">
        Choose how you'd like to start. You can launch right away or let a Rentid expert guide you
        through pre-boarding.
      </p>
    </div>

    <div className="preob-chooser__grid">
      <button
        type="button"
        onClick={() => onSelect('start-now')}
        className="preob-choice preob-choice--primary"
      >
        <img src={`${process.env.PUBLIC_URL}/rentid-r-icon.svg`} alt="Rentid" className="preob-choice__icon-img" />
        <h2 className="preob-choice__title">Start my onboarding now</h2>
        <p className="preob-choice__copy">
          Jump straight into the RentDirect app and set up your fleet yourself. Best if you're ready
          to go and have your details handy.
        </p>
        <span className="preob-choice__cta">
          Continue
          <ArrowRight size={16} className="preob-choice__arrow" />
        </span>
      </button>

      <button
        type="button"
        onClick={() => onSelect('preboard')}
        className="preob-choice"
      >
        <span className="preob-choice__icon">
          <User size={24} />
        </span>
        <h2 className="preob-choice__title">Begin pre-boarding</h2>
        <p className="preob-choice__copy">
          Share your business info and vehicles up front, then schedule onboarding with a Rentid expert
          who'll get everything set up with you.
        </p>
        <span className="preob-choice__cta">
          Schedule onboarding with a Rentid Expert
          <ArrowRight size={16} className="preob-choice__arrow" />
        </span>
      </button>
    </div>
  </div>
);

const StartNow = () => (
  <div className="preob-startnow">
    <div className="preob-startnow__head">
      <span className="preob-startnow__icon">
        <Rocket size={24} />
      </span>
      <h1 className="preob-startnow__title">Before you sign up</h1>
      <p className="preob-startnow__sub">
        Have these ready prior to account set up to breeze through self onboarding.
      </p>
    </div>

    <ul className="preob-checklist">
      {STARTUP_CHECKLIST.map((item) => (
        <li key={item.title} className="preob-checklist__item">
          <span className="preob-checklist__check">
            <Check size={14} strokeWidth={2.5} />
          </span>
          <div>
            <p className="preob-checklist__title">{item.title}</p>
            {item.desc && <p className="preob-checklist__desc">{item.desc}</p>}
            {item.details && (
              <ul className="preob-checklist__details">
                {item.details.map((d) => (
                  <li key={d.label} className="preob-checklist__detail">
                    <span className="preob-checklist__detail-label">{d.label}:</span> {d.text}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </li>
      ))}
    </ul>

    <div className="preob-startnow__cta-row">
      <a href={APP_URL} target="_blank" rel="noopener noreferrer" className="preob-btn preob-btn--primary preob-startnow__cta">
        Continue to Sign Up
        <ExternalLink size={16} />
      </a>
    </div>
  </div>
);

const GetStartedFlow = () => {
  const [view, setView] = useState('choose');

  return (
    <div className="preob-flow">
      <div className="preob-flow__topbar">
        <Link to="/get-started" className="preob-flow__back">
          <ArrowLeft size={16} />
          Back to home
        </Link>
        {view !== 'choose' && (
          <button type="button" onClick={() => setView('choose')} className="preob-flow__change">
            Change option
          </button>
        )}
      </div>

      {view === 'choose' && <Chooser onSelect={setView} />}
      {view === 'start-now' && <StartNow />}
      {view === 'preboard' && <OnboardingForm />}
    </div>
  );
};

export default GetStartedFlow;
